import { onDestroy } from 'svelte'

/**
 * Manages the lifetime of a view's in-flight requests.
 *
 * `start()` aborts whatever request scope was open before and returns a fresh
 * `AbortSignal` to pass to every API call that belongs to the new load. That
 * gives two things for free:
 *
 * - **No out-of-order results.** A second load (e.g. the user changes a
 *   filter) aborts the first, so a slow earlier response can't land after — and
 *   overwrite — the newer one.
 * - **No work after teardown.** The current scope is aborted when the component
 *   is destroyed (navigating away), so an abandoned fetch stops instead of
 *   resolving into a gone component.
 *
 * Callers pass one signal to all calls of a single load (parallel context
 * fetches included) and guard their `catch`/`finally` with `signal.aborted`
 * so a deliberate cancellation is not surfaced as an error.
 */
export function useAbortable(): { start: () => AbortSignal; abort: () => void } {
  let controller: AbortController | null = null

  function abort(): void {
    controller?.abort()
    controller = null
  }

  function start(): AbortSignal {
    abort()
    controller = new AbortController()
    return controller.signal
  }

  onDestroy(abort)

  return { start, abort }
}
